/**
 * WHICH TAX YEAR THE TWO LIST PAGES OPEN ON. (ZEN-762)
 *
 * The current calendar year — the year a withdrawal can be taken in. If that
 * year has nothing to show yet, the most recent year that does, as the
 * Overview does: not opening on an empty page is not guessing when a family
 * files (ZEN-692). (Owner rulings, 2026-10-12.)
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export const currentTaxYear = () => String(new Date().getFullYear());

/**
 * `years` is every tax year that has something to show.
 *
 *   the current year has something          → the current year
 *   otherwise, an earlier year has          → the latest earlier year
 *   otherwise, only later years have        → the nearest later year
 *   nothing anywhere                        → "" (every year), so a family
 *                                             with no records reads "yet"
 */
export function yearToOpenOn(years, current = currentTaxYear()) {
  const now = Number(current);
  const have = [...new Set((years || []).map(Number).filter((y) => Number.isInteger(y) && y > 0))];
  if (have.length === 0) return "";
  if (have.includes(now)) return String(now);
  const earlier = have.filter((y) => y < now);
  if (earlier.length) return String(Math.max(...earlier));
  return String(Math.min(...have));
}

/**
 * What the address the Expenses page was opened with already decides.
 *
 *   a link that names a year      → that year
 *   a link that came for a reason → every year ("")
 *   anything else                 → undefined: not decided by the address
 *
 * A link from elsewhere in the app ("Review now", "show me which one") promises
 * particular expenses, and they can be from any year.
 */
export function yearFromAddress(searchParams) {
  const named = searchParams?.get("tax_year");
  if (named) return named;
  const cameForAReason = ["tab", "missing_receipt", "needs_rate", "attention"].some((k) => searchParams?.get(k));
  return cameForAReason ? "" : undefined;
}

/**
 * The Expenses page's opening year, or undefined while it is being worked out.
 *
 * Asks which years this beneficiary has expenses in — unless the address
 * already decided. If that question fails, the current year: never a blank page
 * waiting on it.
 *
 * NOT FROZEN AT THE FIRST ANSWER. The question is asked again whenever the
 * expenses change, so a page that opened on last year because this year was
 * empty moves to this year the moment its first expense is saved — otherwise
 * the expense just added would not be on screen. Once a year is picked by hand
 * the page keeps that instead; this only speaks until then.
 */
export function useExpensesOpeningYear(searchParams, studentId) {
  // Read once: the page clears its address after reading it.
  const [fromAddress] = useState(() => yearFromAddress(searchParams));
  const ask = fromAddress === undefined;
  const { data, isError } = useQuery({
    // Under "expenses", so everything that refreshes the list refreshes this.
    queryKey: ["expenses", "years-with-anything", studentId || "all"],
    queryFn: async () => {
      const p = new URLSearchParams({ include_drafts: "false" });
      if (studentId) p.set("student_id", studentId);
      return (await api.get(`/expenses?${p}`)).data;
    },
    enabled: ask,
    retry: 1,
  });
  if (!ask) return fromAddress;
  if (data !== undefined) return yearToOpenOn((data || []).map((e) => e.eligibility_tax_year));
  return isError ? currentTaxYear() : undefined;
}
